import { actionAlert } from "@suraasa/placebo-ui"
import api from "api"
import { CartlessOrder } from "api/resources/orders/types"
import CpatLogo from "features/Cpat/assets/cpat-mini-logo.webp"
import { useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import routes from "routes"
import { getRemoteMedia } from "utils/getRemoteMedia"
import { mapErrorsV2 } from "utils/helpers"
import { useEnrollments } from "utils/hooks/useEnrollments"

import { usePayment } from "../hooks/usePayment"

type Props = {
  purchaseDetails: {
    itemName: string
    amount: number
    currency: {
      code: string
      symbol: string
    }
  } | null
}

export const useCPATPayment = (props: Props) => {
  const [stripeForm, setStripeForm] = useState<JSX.Element | null>(null)

  const [order, setOrder] = useState<CartlessOrder | null>(null)

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const enrollments = useEnrollments()

  const onSuccess = async () => {
    const handleClose = () => {
      enrollments.refetch()
      const next = searchParams.get("next")
      navigate(next || routes.cpat.dashboard, { replace: true })
    }

    await actionAlert.success({
      title: "Payment Successful!",
      duration: 5000,
      onClose: handleClose,
    })

    handleClose()
  }

  const paymentPlanId = order?.paymentPlans[0].id

  const { checkout } = usePayment({
    stripeConfig: {
      paymentKey: import.meta.env.VITE_STRIPE_PAYMENT_KEY,
      logo: () => <img src={CpatLogo} alt="logo" className="me-1.5 size-10" />,
      getForm: setStripeForm,
      handleClose: () => {
        window.location.reload()
        setStripeForm(null)
      },
      // onClientSecretGenerated: secret =>
      //   sessionStorage.setItem("stripeClientSecret", secret),
      redirectURL: getStripeSuccessRedirectURL(),
    },
    razorpayConfig: {
      logo: getRemoteMedia(`product-thumbnails/cpat`, {
        height: 48,
        width: 48,
      }),
      successHandler: () => {
        onSuccess()
      },
    },
    successURL: getStripeSuccessRedirectURL(),
    purchaseDetails: props.purchaseDetails,
  })

  const createOrder = async (billingAddressId: string, productSlug: string) => {
    try {
      const res = await api.orders.createOrder({
        data: {
          productSlug,
          billingAddressId,
        },
      })
      if (res.paymentPlans.length === 0) {
        throw new Error("No payment plans found")
      }
      setOrder(res)
      return res
    } catch (error) {
      mapErrorsV2(error)
    }
  }

  return {
    createOrder,
    checkout: async (promocode: string | null) => {
      if (!paymentPlanId) throw new Error("Payment plan ID not found")
      await checkout(paymentPlanId, {
        promocode,
      })
    },
    stripePaymentElement: stripeForm,
    orderId: order?.id,
  }
}

const getStripeSuccessRedirectURL = () => {
  const next =
    new URLSearchParams(window.location.search).get("next") ||
    routes.cpat.dashboard

  return new URL(next, window.location.origin).toString()
}
